import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format time in minutes and seconds
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Calculate percentage
export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

// Format date
export function formatDate(date: Date, locale?: string): string {
  const loc = locale === 'en' ? 'en-US' : 'fr-FR';
  return new Intl.DateTimeFormat(loc, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

// Shuffle array (for randomizing questions)
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Get difficulty color
export function getDifficultyColor(difficulty: 'easy' | 'medium' | 'hard'): string {
  switch (difficulty) {
    case 'easy':
      return 'text-green-400 bg-green-500/10 border-green-500/20';
    case 'medium':
      return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
    case 'hard':
      return 'text-red-400 bg-red-500/10 border-red-500/20';
  }
}

export function getDifficultyLabel(difficulty: 'easy' | 'medium' | 'hard'): string {
  switch (difficulty) {
    case 'easy': return 'Facile';
    case 'medium': return 'Moyen';
    case 'hard': return 'Difficile';
  }
}

export function getDominantDifficulty(difficulties: Array<'easy' | 'medium' | 'hard'>): 'easy' | 'medium' | 'hard' | null {
  if (difficulties.length === 0) return null;
  const counts = { easy: 0, medium: 0, hard: 0 };
  for (const d of difficulties) counts[d]++;
  return (Object.entries(counts) as Array<['easy' | 'medium' | 'hard', number]>)
    .sort((a, b) => b[1] - a[1])[0][0];
}

// Calculate grade based on score
export function calculateGrade(score: number): { grade: string; color: string } {
  if (score >= 90) return { grade: 'A+', color: 'text-green-600' };
  if (score >= 85) return { grade: 'A', color: 'text-green-600' };
  if (score >= 80) return { grade: 'B+', color: 'text-blue-600' };
  if (score >= 75) return { grade: 'B', color: 'text-blue-600' };
  if (score >= 70) return { grade: 'C+', color: 'text-yellow-600' };
  if (score >= 65) return { grade: 'C', color: 'text-yellow-600' };
  if (score >= 60) return { grade: 'D', color: 'text-orange-600' };
  return { grade: 'F', color: 'text-red-600' };
}
